import { useEffect, useRef, useState } from 'react'
import { getStatus, guessDeviceName, login, requestReset, type ServerStatus } from '../lib/api'
import { useStore } from '../state/store'
import { Logo } from '../components/Logo'

/**
 * Remembered separately from the synced store, and deliberately kept across
 * sign-out: it is a convenience belonging to this browser, not account data. A
 * returning device should only have to type a password.
 */
const LAST_EMAIL_KEY = 'language-island:last-email'

function readLastEmail(): string {
  try {
    return localStorage.getItem(LAST_EMAIL_KEY) ?? ''
  } catch {
    return ''
  }
}

/**
 * Sign-in turns on sync. The app is fully usable without it, so this screen
 * never blocks the drills — but it is reachable in one click from the rail and
 * from the dashboard, because an account nobody can find is an account nobody
 * uses.
 */
export function SignInView({ onDone }: { onDone: () => void }) {
  const { dispatch } = useStore()
  const [status, setStatus] = useState<ServerStatus | null>(null)
  const [email, setEmail] = useState(readLastEmail)
  const [password, setPassword] = useState('')
  const [deviceName, setDeviceName] = useState(guessDeviceName())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)
  const [resetting, setResetting] = useState(false)

  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void getStatus().then((result) => {
      if (result.ok) setStatus(result)
      else setError(result.error)
    })
  }, [])

  // Land the cursor where there is actually work to do.
  useEffect(() => {
    if (readLastEmail()) passwordRef.current?.focus()
    else emailRef.current?.focus()
  }, [])

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault()
    if (busy) return

    /*
     * Read from the fields, not only from state. A password manager fills them
     * without always firing onChange, and the previous guard returned silently
     * in that case — a button that clicks and does nothing, with no way to tell
     * it apart from a broken server.
     */
    const typedEmail = (emailRef.current?.value ?? email).trim()
    const typedPassword = passwordRef.current?.value ?? password

    if (!typedEmail.includes('@')) {
      setError('That does not look like an email address.')
      emailRef.current?.focus()
      return
    }
    if (!typedPassword) {
      setError('Enter your password.')
      passwordRef.current?.focus()
      return
    }

    setBusy(true)
    setError(null)
    const result = await login(typedEmail, typedPassword, deviceName)
    setBusy(false)

    if (!result.ok) {
      setError(result.error)
      passwordRef.current?.select()
      return
    }

    try {
      localStorage.setItem(LAST_EMAIL_KEY, result.user.email)
    } catch {
      // A browser refusing storage should not block a successful sign-in.
    }

    dispatch({
      type: 'patchSync',
      patch: {
        token: result.token,
        deviceId: result.deviceId,
        deviceName: result.deviceName,
        accountEmail: result.user.email,
        lastError: null,
        // A fresh sign-in starts from zero so this device pulls the whole
        // account, rather than trusting a cursor from a previous session.
        cursor: 0,
        lastPushedAt: 0,
        // Nothing may be pushed at this account before it is pulled.
        lastSyncAt: null,
      },
    })
    onDone()
  }

  return (
    <div className="signin">
      <div className="signin-head">
        <Logo size={44} />
      </div>

      <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
        Sign in
      </h1>
      <p className="lede" style={{ marginBottom: '1.4rem' }}>
        Keeps your islands on every device you use. The drills work without it — sync is an
        addition, not a requirement.
      </p>

      {error && (
        <div className="notice is-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {status && !status.configured && (
        <div className="notice is-error" style={{ marginBottom: '1rem' }}>
          Sync is switched off on this server — no signing secret is set.
        </div>
      )}

      {status && status.configured && !status.hasAccounts && (
        <div className="notice" style={{ marginBottom: '1rem' }}>
          No accounts exist yet. Create the first with <span className="data">npm run user:add</span>.
        </div>
      )}

      {/* A real form, so password managers offer to fill and save it, and so
          Enter submits from any field without hand-rolled key handling. */}
      <form className="card" onSubmit={submit}>
        <label className="field">
          <span className="field-label">Email</span>
          <input
            ref={emailRef}
            className="input"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>

        <label className="field">
          <span className="field-label">Password</span>
          <input
            ref={passwordRef}
            className="input is-data"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        <details className="signin-more">
          <summary>Name this device — {deviceName}</summary>
          <input
            className="input"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder="Android phone"
            style={{ marginTop: '0.5rem' }}
          />
          <p className="muted" style={{ fontSize: '0.82rem', margin: '0.4rem 0 0' }}>
            Shown in Settings so you can sign out a device you no longer have.
          </p>
        </details>

        <div className="row" style={{ marginTop: '1rem' }}>
          <button
            type="submit"
            className="btn is-primary"
            disabled={busy}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <button type="button" className="btn is-quiet" onClick={onDone}>
            Not now
          </button>
        </div>
      </form>

      {/* The other direction. Someone who lands on sign-in without an account —
          from a bookmark, or by pressing "I already have an account" too
          eagerly — needs a way back to the front door. */}
      <p className="muted" style={{ fontSize: '0.86rem', marginTop: '1rem' }}>
        No account yet?{' '}
        <button
          type="button"
          className="btn is-small is-quiet"
          onClick={() => {
            try {
              localStorage.removeItem('language-island:looked-around')
            } catch {
              // Nothing to clear; the reload still lands on the welcome screen.
            }
            window.location.reload()
          }}
        >
          Start a free week
        </button>
      </p>

      {resetSent ? (
        /* The same message whether or not that address has an account. Saying
           "no such account" here would turn this form into a way of testing
           whether somebody is a customer. */
        <p className="notice" style={{ fontSize: '0.86rem', marginTop: '1rem' }}>
          If that address has an account, a reset link is on its way. It expires in an hour, and it
          is worth checking the spam folder.
        </p>
      ) : (
        <p className="muted" style={{ fontSize: '0.84rem', marginTop: '1rem' }}>
          Forgotten the password?{' '}
          <button
            type="button"
            className="btn is-small is-quiet"
            disabled={resetting || !email.includes('@')}
            title={email.includes('@') ? undefined : 'Enter your email address first.'}
            onClick={async () => {
              setResetting(true)
              await requestReset(email.trim())
              setResetting(false)
              // Shown regardless of the result, for the same reason.
              setResetSent(true)
            }}
          >
            {resetting ? 'Sending…' : 'Email me a link'}
          </button>
        </p>
      )}
    </div>
  )
}
